import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ChartCard } from "@repo/ui/Cards/chartCard";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useState, type FC } from "react";
import { useCreateFunnelMutation } from "../../../api/entities/funnel.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { FunnelSettingsFields } from "./funnelSettingsFields.js";

export interface FunnelCreateFormProps {
  teammateAgentId: TeammateId;
}

/** Shown while the agent has no funnel: creating one starts generating it. */
export const FunnelCreateForm: FC<FunnelCreateFormProps> = (props) => {
  const { teammateAgentId } = props;

  const [sellingStrategy, setSellingStrategy] = useState("");
  const [playbookIds, setPlaybookIds] = useState<readonly PlaybookId[]>([]);
  const [createFunnel, { isLoading: isCreating }] = useCreateFunnelMutation();
  const canEdit = useTeammateAuthorization().can(
    PermissionActions.UPDATE,
    PermissionSubjects.TEAMMATES,
  );

  const handleCreate = async () => {
    try {
      await createFunnel({
        voiceAgentTeammateIds: [teammateAgentId],
        playbookIds: [...playbookIds],
        sellingStrategy,
      }).unwrap();
    } catch (error) {
      console.error("Failed to create funnel:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Couldn't create the funnel",
        description: "Please try again.",
      });
    }
  };

  return (
    <ChartCard
      title="Create a funnel"
      subtitle="A funnel shows the stages this agent's interactions move through, and the moments of friction in each stage, such as callers' questions and objections or where the agent breaks down. Generating the stages and moments takes a few minutes."
    >
      <div className="flex flex-col gap-4">
        <FunnelSettingsFields
          sellingStrategy={sellingStrategy}
          onSellingStrategyChange={setSellingStrategy}
          playbookIds={playbookIds}
          onPlaybookIdsChange={setPlaybookIds}
          isDisabled={!canEdit || isCreating}
        />
        <div className="flex justify-end">
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => void handleCreate()}
            isLoading={isCreating}
            isDisabled={!canEdit || sellingStrategy.trim() === ""}
          >
            Create funnel
          </Button>
        </div>
      </div>
    </ChartCard>
  );
};
